import type { RbimHouseholdProfile, RbimResidentProfile } from "../desktop-add-new-data/lib/rbim-storage";
import {
  filterResidentsByScope,
  flattenReportColumns,
  generateReportData,
  type ReportColumn,
  type ReportScopeOptions,
  type ReportTableData,
} from "../desktop-add-new-data/modules/reports/report-calculations";

/**
 * Return only the age brackets that are actually rendered as rows in the
 * selected statistical report. This keeps View Names tied to the report table
 * instead of to a separate hard-coded age definition.
 */
export function getReportAgeBrackets(reportData: ReportTableData) {
  const ageColumn = findAgeColumn(reportData.columns);
  if (!ageColumn) return [];

  return reportData.rows
    .filter((row) => !row.isTotal)
    .map((row) => String(row[ageColumn.key] ?? "").trim())
    .filter((label, index, values) => isAgeBracketLabel(label) && values.indexOf(label) === index);
}

/**
 * Resolve the names for one clickable age row by running the same report
 * calculation against each scoped resident. A resident is included only when
 * that resident contributes a positive count to the selected age row, so the
 * names inherit the report's own eligibility rules and classifications rather
 * than being filtered by age alone.
 */
export function getResidentsForReportAgeBracket(
  reportId: number,
  ageBracket: string,
  residents: RbimResidentProfile[],
  households: RbimHouseholdProfile[],
  scope: ReportScopeOptions,
) {
  return filterResidentsByScope(residents, scope)
    .filter((resident) => residentContributesToAgeRow(
      resident,
      reportId,
      ageBracket,
      households,
      scope,
    ))
    .sort((left, right) => {
      const byName = left.fullName.localeCompare(right.fullName, undefined, { sensitivity: "base" });
      if (byName !== 0) return byName;
      return left.householdCode.localeCompare(right.householdCode, undefined, { sensitivity: "base" });
    });
}

function residentContributesToAgeRow(
  resident: RbimResidentProfile,
  reportId: number,
  ageBracket: string,
  households: RbimHouseholdProfile[],
  scope: ReportScopeOptions,
) {
  const residentHouseholds = households
    .filter((household) => household.householdId === resident.householdId)
    .map((household) => ({
      ...household,
      members: household.members.filter((member) => member.residentId === resident.residentId),
      totalMembers: 1,
    }));

  const isolatedReport = generateReportData(
    reportId,
    scope,
    [resident],
    residentHouseholds,
  );
  const ageColumn = findAgeColumn(isolatedReport.columns);
  if (!ageColumn) return false;

  const targetRow = isolatedReport.rows.find((row) => {
    if (row.isTotal) return false;
    return normalizeAgeLabel(String(row[ageColumn.key] ?? "")) === normalizeAgeLabel(ageBracket);
  });
  if (!targetRow) return false;

  const measureColumns = flattenReportColumns(isolatedReport.columns)
    .filter((column) => column.key !== ageColumn.key);

  return measureColumns.some((column) => {
    const value = targetRow[column.key];
    return typeof value === "number" ? value > 0 : Number(value) > 0;
  });
}

function findAgeColumn(columns: ReportColumn[]) {
  const flat = flattenReportColumns(columns);
  return flat.find((column) => column.label.trim().toLowerCase() === "age");
}

function isAgeBracketLabel(label: string) {
  const normalized = normalizeAgeLabel(label);
  return /^\d+-\d+$/.test(normalized)
    || /^\d+\+$/.test(normalized)
    || /^\d+-\d+months?$/.test(normalized);
}

function normalizeAgeLabel(value: string) {
  return value.trim().toLowerCase().replace(/\s+/g, "");
}
